import React, { useCallback, useEffect, useState } from "react";
import { Form, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import Loading from "../../../components/LoadingPage/Loading";
import ItemListingTitle from "../../../components/itemListingTitle/itemListingTitle";
import {
  useGetMckessonEdiFileListMutation,
  useGetMckessonEdiItemListMutation,
  useFetchMckessonOrderItemMutation,
  useMarkMckessonOrderProcessedMutation,
} from "../../../redux/mckesson/mckessonApiSlice";
import { getErrorMessage } from "../../../utils/commonUtils";
import "./McOrderProcessor.scss";

interface OrderFile {
  id: string;
  fileName: string;
  fileContent: string;
  timestamp: string;
  loaded: boolean;
  processed: boolean;
  isActive: boolean;
  createdOnUtc: string;
  updatedOnUtc: string;
  createdBy: string;
  updatedBy: string;
}

interface OrderDataItem {
  id: string;
  hlId: number;
  vc: string;
  mg: string;
  lot: string;
  ndc: string;
  qtyOrdered: number;
  qtyShipped: number;
  uom: string;
  qtyShippedInvUom: number;
  qtyReceived: number;
  invUom: string;
  itemDescription: string;
  expDate: string;
  isProcessed: boolean;
  reason: number;
}

interface OrderInfo {
  orderNumber: string;
  orderDate: string;
  trackingNumber: string;
  fileName: string;
  orderingLocationCode: string;
  orderingLocationName: string;
}

const defaultFilter = { searchText: "", active: true };

const McOrderProcessor: React.FC = () => {
  const [searchVal, setSearchVal] = useState({ ...defaultFilter });
  const [selectedStock, setSelectedStock] = useState("");
  const [orderFiles, setOrderFiles] = useState<OrderFile[]>([]);
  const [dataItems, setDataItems] = useState<OrderDataItem[]>([]);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string>("");

  const [getEdiFileList, { isLoading: filesLoading }] =
    useGetMckessonEdiFileListMutation();
  const [getEdiItemList, { isLoading: dataItemsLoading }] =
    useGetMckessonEdiItemListMutation();
  const [fetchOrderItem, { isLoading: fetchLoading }] =
    useFetchMckessonOrderItemMutation();
  const [markProcessed, { isLoading: markProcessedLoading }] =
    useMarkMckessonOrderProcessedMutation();

  const isLoading =
    filesLoading || dataItemsLoading || fetchLoading || markProcessedLoading;

  const loadFiles = useCallback(async () => {
    try {
      const res = await getEdiFileList({
        searchText: searchVal.searchText,
        active: searchVal.active,
      }).unwrap();
      if (res?.result) {
        setOrderFiles(res.result);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [getEdiFileList, searchVal]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleFileClick = async (file: OrderFile) => {
    setSelectedFileId(file.id);
    try {
      const res = await getEdiItemList({
        searchText: file.id,
        active: true,
      }).unwrap();
      if (res?.result) {
        setDataItems(res.result.items ?? []);
        setOrderInfo(res.result.orderInfo ?? null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleFetch = async (item: OrderDataItem) => {
    try {
      const res = await fetchOrderItem({
        fileId: selectedFileId,
        itemId: item.id,
        stock: selectedStock,
      }).unwrap();
      if (res?.success) {
        toast.success(res.message || "Item fetched successfully");
        handleFileClick({ id: selectedFileId } as OrderFile);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMarkProcessed = async (item: OrderDataItem) => {
    try {
      const res = await markProcessed({
        fileId: selectedFileId,
        itemId: item.id,
      }).unwrap();
      if (res?.success) {
        toast.success(res.message || "Item marked as processed");
        handleFileClick({ id: selectedFileId } as OrderFile);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="mc-order-processor-wrapper">
      {isLoading && <Loading />}

      <ItemListingTitle
        title="McKesson Order Processor"
        disRecords={orderFiles.length}
        totalRecords={orderFiles.length}
        searchVal={searchVal}
        setSearchVal={setSearchVal}
        defaultFilter={defaultFilter}
        isAddPermitted={false}
        checkboxLabel="Pending"
        clearButtonText="Clear All / Refresh"
      />

      {/* Files Table */}
      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            <tr>
              <th>File Name</th>
              <th>Timestamp</th>
              <th>Processed?</th>
              <th>Created By</th>
              <th>Edited By</th>
            </tr>
          </thead>
          <tbody>
            {orderFiles.length > 0 ? (
              orderFiles.map((file) => (
                <tr
                  key={file.id}
                  className={selectedFileId === file.id ? "selected" : ""}
                  onClick={() => handleFileClick(file)}
                >
                  <td>
                    <span className="file-link">{file.fileName}</span>
                  </td>
                  <td>{file.timestamp}</td>
                  <td>{file.processed ? "Yes" : "No"}</td>
                  <td>{file.createdBy}</td>
                  <td>{file.updatedBy}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Data Items Section */}
      {orderInfo && (
        <div className="data-items-section">
          <h5 className="section-title">Data Items</h5>

          {/* Order Info */}
          <div className="order-info-grid">
            <div className="order-info-row">
              <div className="info-item">
                <label>Order # (SRX):</label>
                <span>{orderInfo.orderNumber}</span>
              </div>
              <div className="info-item">
                <label>Order Date:</label>
                <span>{orderInfo.orderDate}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Tracking #:</label>
                <span>{orderInfo.trackingNumber}</span>
              </div>
              <div className="info-item">
                <label>File Name:</label>
                <span>{orderInfo.fileName}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Ordering Location Code:</label>
                <span>{orderInfo.orderingLocationCode}</span>
              </div>
              <div className="info-item">
                <label>Ordering Location Name:</label>
                <span>{orderInfo.orderingLocationName}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Stock:</label>
                <Form.Select
                  size="sm"
                  value={selectedStock}
                  onChange={(e) => setSelectedStock(e.target.value)}
                  className="stock-select"
                >
                  <option value="">Select</option>
                </Form.Select>
              </div>
            </div>
          </div>

          {/* Data Items Table */}
          <div className="listing-table-wrapper">
            <Table responsive="md" hover={true}>
              <thead>
                <tr>
                  <th>HL Id</th>
                  <th>VC</th>
                  <th>MG</th>
                  <th>Lot</th>
                  <th>NDC</th>
                  <th>Qty Ordered</th>
                  <th>Qty Shipped</th>
                  <th>UOM</th>
                  <th>Qty Shipped (Inv. Uom)</th>
                  <th>Qty Received</th>
                  <th>Inv. Uom</th>
                  <th>ItemDescription</th>
                  <th>ExpDate</th>
                  <th>Processed?</th>
                  <th>Reason</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {dataItems.length > 0 ? (
                  dataItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{item.hlId}</td>
                      <td>{item.vc}</td>
                      <td>{item.mg}</td>
                      <td>{item.lot}</td>
                      <td>{item.ndc}</td>
                      <td>{item.qtyOrdered?.toFixed(2)}</td>
                      <td>{item.qtyShipped?.toFixed(2)}</td>
                      <td>{item.uom}</td>
                      <td>{item.qtyShippedInvUom?.toFixed(2)}</td>
                      <td>{item.qtyReceived?.toFixed(2)}</td>
                      <td>{item.invUom}</td>
                      <td>{item.itemDescription}</td>
                      <td>{item.expDate}</td>
                      <td>{item.isProcessed ? "Yes" : "No"}</td>
                      <td>{item.reason}</td>
                      <td className="action-cell">
                        <span
                          className="action-link"
                          onClick={() => handleFetch(item)}
                        >
                          Fetch
                        </span>
                        <span
                          className="action-link"
                          onClick={() => handleMarkProcessed(item)}
                        >
                          Mark Processed
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={16} className="text-center">
                      No data items found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default McOrderProcessor;
